import {
  ArrowDownLeft,
  ArrowUpRight,
  Check,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  User,
  X,
} from "lucide-react";
import { useState } from "react";
import { OutreachDisclaimer } from "../outreach/OutreachDisclaimer";
import { splitSubject } from "../outreach/outreachDrafts";

interface OutreachAttempt {
  id: string;
  channel: string;
  direction: string;
  status: string;
  messageContent: string | null;
  responseContent: string | null;
  sentAt: string | null;
  respondedAt: string | null;
  sentBy: { id: string; name: string };
  createdAt: string;
  createdVia?: string;
}

interface OutreachTimelineProps {
  attempts: OutreachAttempt[];
  campaignId?: string;
  onUpdate?: (
    attemptId: string,
    data: { status?: string; responseContent?: string; respondedAt?: string },
  ) => Promise<void>;
}

const CHANNEL_ICONS: Record<string, React.ReactNode> = {
  email: <Mail size={12} />,
  phone: <Phone size={12} />,
  linkedin: <MessageSquare size={12} />,
  other: <User size={12} />,
};

// The contact log's vocabulary (mirrors OutreachAttempt::STATUSES). Anything
// else is a legacy value and falls back to the neutral style — never to the
// blue "sent" style, which is what made "pending" look sent (#3169).
const UNKNOWN_STATUS_STYLE = "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
const STATUS_COLORS: Record<string, string> = {
  sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  replied: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  bounced: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  no_response: UNKNOWN_STATUS_STYLE,
};

export const OUTREACH_STATUS_OPTIONS = ["sent", "replied", "bounced", "no_response"];

// Same treatment as OutreachForm's controls so they read in both themes.
const CONTROL_CLASSES =
  "rounded border border-input-border bg-card text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent";

export function OutreachTimeline({ attempts, campaignId, onUpdate }: OutreachTimelineProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ status: "", responseContent: "" });
  const [saving, setSaving] = useState(false);

  if (attempts.length === 0) {
    return (
      <div className="py-2 text-center">
        <p className="text-xs text-text-faint">No contact logged yet.</p>
        <OutreachDisclaimer campaignId={campaignId} className="mt-1 justify-center" />
      </div>
    );
  }

  const startEdit = (attempt: OutreachAttempt) => {
    setEditForm({
      status: attempt.status,
      responseContent: attempt.responseContent || "",
    });
    setEditingId(attempt.id);
  };

  const handleSave = async (attemptId: string) => {
    if (!onUpdate) return;
    setSaving(true);
    try {
      const data: { status?: string; responseContent?: string; respondedAt?: string } = {};
      const original = attempts.find((a) => a.id === attemptId);
      if (editForm.status !== original?.status) data.status = editForm.status;
      if (editForm.responseContent !== (original?.responseContent || "")) {
        data.responseContent = editForm.responseContent;
        if (editForm.responseContent && !original?.respondedAt) {
          data.respondedAt = new Date().toISOString();
        }
      }
      await onUpdate(attemptId, data);
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      {attempts.map((attempt) => {
        const isEditing = editingId === attempt.id;
        return (
          <div key={attempt.id} className="flex items-start gap-2 rounded border border-border p-2">
            <div className="mt-0.5 flex-shrink-0 text-text-faint">
              {CHANNEL_ICONS[attempt.channel] ?? <Mail size={12} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {attempt.direction === "outbound" ? (
                  <ArrowUpRight size={10} className="text-blue-500" />
                ) : (
                  <ArrowDownLeft size={10} className="text-green-500" />
                )}
                <span className="text-xs font-medium text-text-secondary">
                  {attempt.channel.replace("_", " ")}
                </span>
                {isEditing ? (
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className={`${CONTROL_CLASSES} px-1.5 py-0.5 text-[10px]`}
                  >
                    {statusOptionsFor(attempt.status).map((s) => (
                      <option key={s} value={s}>
                        {s.replace("_", " ")}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${STATUS_COLORS[attempt.status] ?? UNKNOWN_STATUS_STYLE}`}
                  >
                    {attempt.status.replace("_", " ")}
                  </span>
                )}
                <span className="text-[10px] text-text-faint">
                  {attempt.sentBy.name}
                  {attempt.createdVia && attempt.createdVia !== "ui" ? " via AI" : ""} &middot;{" "}
                  {new Date(attempt.sentAt ?? attempt.createdAt).toLocaleDateString()}
                </span>
                {onUpdate && !isEditing && (
                  <button
                    type="button"
                    onClick={() => startEdit(attempt)}
                    className="rounded p-0.5 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                    title="Edit"
                  >
                    <Pencil size={10} />
                  </button>
                )}
                {isEditing && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => handleSave(attempt.id)}
                      disabled={saving}
                      className="rounded p-0.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                      title="Save"
                    >
                      <Check size={10} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="rounded p-0.5 text-text-faint hover:bg-card-hover"
                      title="Cancel"
                    >
                      <X size={10} />
                    </button>
                  </div>
                )}
              </div>
              <LoggedMessage content={attempt.messageContent} />
              {isEditing ? (
                <textarea
                  value={editForm.responseContent}
                  onChange={(e) => setEditForm({ ...editForm, responseContent: e.target.value })}
                  placeholder="Response content..."
                  rows={2}
                  className={`${CONTROL_CLASSES} mt-1 w-full px-2 py-1 text-xs`}
                />
              ) : (
                attempt.responseContent && (
                  <p className="mt-1 line-clamp-2 rounded bg-green-50 px-2 py-1 text-xs text-green-700 dark:bg-green-900/20 dark:text-green-400">
                    {attempt.responseContent}
                  </p>
                )
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// A legacy status (pre-#3169 "pending") stays selectable so the picker shows
// what is actually stored instead of silently displaying the first option.
function statusOptionsFor(current: string): string[] {
  return OUTREACH_STATUS_OPTIONS.includes(current)
    ? OUTREACH_STATUS_OPTIONS
    : [current, ...OUTREACH_STATUS_OPTIONS];
}

// Logged emails carry "Subject: …" on their first line (draftText, and the
// old chat-agent drafts). Show it as a subject line rather than letting the
// paragraph collapse it into the body.
function LoggedMessage({ content }: { content: string | null }) {
  if (!content) return null;
  const { subject, body } = splitSubject(content);
  return (
    <div className="mt-1 text-xs text-text-secondary">
      {subject && (
        <p className="truncate" title={subject}>
          <span className="font-medium text-text-primary">Subject:</span> {subject}
        </p>
      )}
      {body && <p className="line-clamp-2 whitespace-pre-line">{body}</p>}
    </div>
  );
}
